/**
 * AnnouncePacket — TLV encoder/decoder for the Bitchat type 0x01 payload.
 *
 * Bitchat announces carry the sender's identity so peers can address and
 * verify them. The payload is a sequence of TLV (Type-Length-Value) fields
 * inside the BitchatPacket payload region:
 *
 *   Offset  Size  Meaning
 *    0       1    type       (tag byte)
 *    1       1    length     (value length in bytes, 0..255)
 *    2       L    value      (length bytes of content)
 *    ...     repeats
 *
 * Tag values (from bitchat/Protocols/Packets.swift):
 *   0x01 — nickname           (UTF-8 string, ≤255 bytes)
 *   0x02 — noisePublicKey     (32 bytes, Curve25519 X25519 raw)
 *   0x03 — signingPublicKey   (32 bytes, Ed25519 raw)
 *   0x04 — directNeighbors    (optional, N × 8-byte peerIDs, max 10)
 *
 * Unknown tags MUST be skipped by the decoder so future extensions do not
 * break older clients.
 */

const TAG = Object.freeze({
  NICKNAME:        0x01,
  NOISE_PUBKEY:    0x02,
  SIGNING_PUBKEY:  0x03,
  DIRECT_NEIGHBORS: 0x04,
});

const MAX_TLV_VALUE = 255;

const encoder = new TextEncoder();
const decoder = new TextDecoder('utf-8', { fatal: false });

/**
 * Encode an announce payload.
 *
 * @param {{
 *   nickname: string,
 *   noisePublicKey?: Uint8Array,
 *   signingPublicKey?: Uint8Array,
 *   directNeighbors?: Uint8Array[],
 * }} fields
 * @returns {Uint8Array}
 */
export function encode(fields) {
  const pieces = [];
  if (fields.nickname) {
    const v = encoder.encode(fields.nickname);
    pieces.push(_tlv(TAG.NICKNAME, v));
  }
  if (fields.noisePublicKey && fields.noisePublicKey.length === 32) {
    pieces.push(_tlv(TAG.NOISE_PUBKEY, fields.noisePublicKey));
  }
  if (fields.signingPublicKey && fields.signingPublicKey.length === 32) {
    pieces.push(_tlv(TAG.SIGNING_PUBKEY, fields.signingPublicKey));
  }
  if (fields.directNeighbors?.length) {
    const joined = _concat(fields.directNeighbors.slice(0, 10));
    pieces.push(_tlv(TAG.DIRECT_NEIGHBORS, joined));
  }
  return _concat(pieces);
}

/**
 * Decode an announce payload. Unknown tags are ignored. Partial / malformed
 * payloads return whatever fields could be parsed plus `ok: false`.
 *
 * @param {Uint8Array} payload
 * @returns {{
 *   ok: boolean,
 *   nickname: string | null,
 *   noisePublicKey: Uint8Array | null,
 *   signingPublicKey: Uint8Array | null,
 *   directNeighbors: Uint8Array[],
 * }}
 */
export function decode(payload) {
  const result = {
    ok: true,
    nickname: null,
    noisePublicKey: null,
    signingPublicKey: null,
    directNeighbors: [],
  };

  let o = 0;
  while (o < payload.length) {
    if (o + 2 > payload.length) { result.ok = false; break; }
    const tag = payload[o++];
    const len = payload[o++];
    if (o + len > payload.length) { result.ok = false; break; }
    const value = payload.subarray(o, o + len);
    o += len;

    switch (tag) {
      case TAG.NICKNAME:
        result.nickname = decoder.decode(value);
        break;
      case TAG.NOISE_PUBKEY:
        if (value.length === 32) result.noisePublicKey = new Uint8Array(value);
        break;
      case TAG.SIGNING_PUBKEY:
        if (value.length === 32) result.signingPublicKey = new Uint8Array(value);
        break;
      case TAG.DIRECT_NEIGHBORS: {
        // packed 8-byte peer IDs
        if (value.length % 8 !== 0) break;
        for (let i = 0; i < value.length; i += 8) {
          result.directNeighbors.push(new Uint8Array(value.subarray(i, i + 8)));
        }
        break;
      }
      default:
        // Unknown tag — skip silently.
        break;
    }
  }

  return result;
}

// ---------- helpers ----------

function _tlv(tag, value) {
  if (value.length > MAX_TLV_VALUE) {
    throw new Error(`AnnouncePacket: TLV value for tag 0x${tag.toString(16)} exceeds ${MAX_TLV_VALUE} bytes`);
  }
  const buf = new Uint8Array(2 + value.length);
  buf[0] = tag;
  buf[1] = value.length;
  buf.set(value, 2);
  return buf;
}

function _concat(arrs) {
  let total = 0;
  for (const a of arrs) total += a.length;
  const out = new Uint8Array(total);
  let o = 0;
  for (const a of arrs) { out.set(a, o); o += a.length; }
  return out;
}

export const TAGS = TAG;
export default { encode, decode, TAGS };
